import React, {FC, useMemo, useState} from "react";
import {Button, Modal, Table, Tag, Tooltip, message} from "antd";
import {ColumnsType} from "antd/lib/table";
import {InfoCircleOutlined} from "@ant-design/icons";
import classNames from "classnames";
import moment from "moment";
import {
    GetBookingByIdWithCourseLessonBookingsDocument,
    PaymentProvider,
    ScheduledPaymentType,
    useCreateNewMolliePaymentAttemptFromAdminMutation,
    useSendMolliePaymentLinkForBookingFromAdminMutation,
} from "../../../../generated/graphql";
import type {GetBookingByIdWithCourseLessonBookingsQuery} from "../../../../generated/graphql";
import {ActionBar} from "./ActionBar";
import {PostenDetail} from "./PostenDetail";
import PanelCard from "../../../../views/PanelCard/PanelCard";
import {BundleLinkModal, BUNDLE_LINK_TOOLTIP} from "./BundleLinkModal";
// Phase 23 / Plan 23-06 / D-10 — Audit-Drawer für PaymentActionLog-Historie.
import {PaymentActionTimelineDrawer} from "../../../../views/PaymentActionTimeline/PaymentActionTimeline";
import {derivePostenStatus, PostenStatus} from "./helpers/derivePostenStatus";
import {mollieMethodLabel} from "../../../../helpers/mollieMethodLabels";
import {renderAmount} from "../../../../helpers/renderAmount";

const css = require("./MolliePaymentSection.less");

// ─── Prop-Shape (D-09 refactor-friendly) ─────────────────────────────────
type _BookingShape = NonNullable<
    GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"]
>;
type ScheduledPaymentItem = NonNullable<_BookingShape["scheduledPayments"]>[number];
type MolliePaymentAttemptItem = NonNullable<
    _BookingShape extends {molliePaymentAttempts: infer T} ? T : never
>[number];

/**
 * Mollie-Zahlungs-Section der Buchungsdetails — Posten-Tabelle.
 *
 * Vorher (Phase 23): EINE Timeline-Card für den jüngsten Attempt mit
 * `isCurrentAttempt=true`. Bei Buchungen mit mehreren Raten (z.B. Monatlich →
 * 2 ScheduledPayments à 175 €, jede mit eigenem aktuellen Attempt) sah der
 * Admin nur eine Rate — B-428 zeigte bei 350 € Buchungswert 175 € an.
 *
 * Jetzt: eine Zeile je ScheduledPayment (Sketch final-b) mit
 *  - Status + Primäraktion pro Posten (`derivePostenStatus`),
 *  - aufklappbarem Detail (Stepper, Stats, volle ActionBar, Refunds, Historie),
 *  - KPI-Leiste (ab 2 Posten) und
 *  - Sammel-Link-Aktion für alle offenen Mollie-Posten.
 */
export type MolliePaymentSectionProps = {
    bookingId: string;
    scheduledPayments: Array<ScheduledPaymentItem>;
    molliePaymentAttempts: Array<MolliePaymentAttemptItem>;
    isCanceled: boolean;
    /**
     * Wartelistenplatz: keine Zahlungslink-Aktionen (createLink/newAttempt/Sammel-Link).
     * Zahlung erst nach Promotion zur Buchung (Incident 18.08.2026).
     */
    isWaitlistBooking?: boolean;
    refetchBooking: () => Promise<unknown>;
    /**
     * True, sobald die Buchung eine Rechnung hat (`booking.invoice?.id` gesetzt).
     * Steuert, ob der Stepper-Step "Abgerechnet" auf `done` (statt `active`) springt.
     */
    bookingFullySettled?: boolean;
    /** E-Mail des Buchers — nur für die Empfängerzeile im Sammel-Link-Modal. */
    bookerEmail?: string | null;
    /**
     * Zahlungsaktionen anbieten (Sammel-Link, Zahlungslink erstellen, ActionBar
     * in Zeile und Detail)? Alle dahinterliegenden Mutationen (`…FromAdmin`)
     * sind auf Admin/Superuser beschränkt — Kursleitungen (Einzelbuchungs-
     * Ansicht) sähen sonst Buttons, die immer in FORBIDDEN enden. Default false.
     */
    canManagePayments?: boolean;
    /** Person nach DSGVO-Löschantrag gelöscht: kein Sammel-Link, kein erster Link, in der ActionBar nur erlaubte Aktionen. */
    attendeeErased?: boolean;
};

interface PostenRow {
    key: string;
    index: number;
    scheduledPayment: ScheduledPaymentItem;
    title: string;
    /** Signierter Betrag (Gutschriften zählen negativ). */
    signedAmount: number;
    currentAttempt: MolliePaymentAttemptItem | null;
    pastAttempts: Array<MolliePaymentAttemptItem>;
    status: PostenStatus;
    /**
     * true, wenn dieser Posten nur ueber `bundleScheduledPaymentIds` eines
     * FREMDEN Anker-Attempts abgedeckt ist (Sammel-Link). Solange der Posten
     * offen ist, bietet die Zeile KEINE eigene Aktion — sonst entstuende ein
     * zweiter Zahlungslink neben dem bereits versendeten Sammel-Link. Bezahlt
     * darf sie erstatten: der Server erstattet pro Rate.
     */
    inBundle: boolean;
}

/** Bündel-IDs eines Attempts als Array (Feld ist nullable — alte Attempts). */
function bundleIdsOf(attempt: {bundleScheduledPaymentIds?: Array<string> | null}): Array<string> {
    return attempt.bundleScheduledPaymentIds || [];
}

function toTime(value: string | Date | null | undefined): number {
    if (!value) return 0;
    const parsed = new Date(value as string | Date);
    const time = parsed.getTime();

    return isNaN(time) ? 0 : time;
}

function isDiscount(sp: {type?: unknown}): boolean {
    return sp.type === ScheduledPaymentType.Discount;
}

/** Gutschriften mindern die Summe — daher negatives Vorzeichen. */
function signedAmount(sp: {type?: unknown}, amount: number): number {
    return isDiscount(sp) ? -Math.abs(amount) : amount;
}

function formatDue(value: string | Date | null | undefined): string {
    if (!value) return "—";
    const parsed = moment(value);

    return parsed.isValid() ? parsed.format("L") : "—";
}

/**
 * Ergaenzt den Zeilen-Status um die Sammel-Link-Information.
 *
 *  - Mitglieds-Zeile (`isBundleMember`): Zweitzeile "im Sammel-Link (n Posten)",
 *    offen KEINE Aktion (Link laeuft ueber die Anker-Zeile), bezahlt bleibt
 *    "Erstatten" — der Server erstattet pro Rate.
 *  - Anker-Zeile (eigener Attempt mit gefuellten `bundleScheduledPaymentIds`):
 *    Hinweis "Sammel-Link (n Posten)" zusaetzlich zur bisherigen Zweitzeile,
 *    Aktionen bleiben unveraendert.
 */
function applyBundleAnnotation(
    status: PostenStatus,
    currentAttempt: {bundleScheduledPaymentIds?: Array<string> | null} | null,
    isBundleMember: boolean,
): PostenStatus {
    if (!currentAttempt) return status;
    const bundleSize = bundleIdsOf(currentAttempt).length;
    if (bundleSize === 0) return status;

    if (isBundleMember) {
        return {
            ...status,
            secondaryLine: `im Sammel-Link (${bundleSize} Posten)`,
            primaryAction: status.isPaid ? status.primaryAction : "none",
        };
    }

    const hint = `Sammel-Link (${bundleSize} Posten)`;

    return {
        ...status,
        secondaryLine: status.secondaryLine ? `${status.secondaryLine} · ${hint}` : hint,
    };
}

export const MolliePaymentSection: FC<MolliePaymentSectionProps> = ({
    bookingId,
    scheduledPayments,
    molliePaymentAttempts,
    isCanceled,
    isWaitlistBooking,
    refetchBooking,
    bookingFullySettled,
    bookerEmail,
    canManagePayments,
    attendeeErased,
}) => {
    // D-10 — Drawer-State für die PaymentActionTimeline. `null` = Drawer zu.
    const [timelineSpId, setTimelineSpId] = useState<string | null>(null);
    const [bundleModalOpen, setBundleModalOpen] = useState<boolean>(false);

    // ─── Defense-in-Depth: Re-Sort nach createDate DESC ──────────────────
    const sortedAttempts = useMemo(() => {
        const copy = [...(molliePaymentAttempts || [])];
        copy.sort((a, b) => toTime(b.createDate) - toTime(a.createDate));

        return copy;
    }, [molliePaymentAttempts]);

    // ─── Posten-Zeilen ───────────────────────────────────────────────────
    const rows: Array<PostenRow> = useMemo(() => {
        const activeSps = (scheduledPayments || []).filter((sp) => !sp.deletedAt);
        const sorted = [...activeSps].sort((a, b) => toTime(a.paymentDue) - toTime(b.paymentDue));

        return sorted.map((sp, position) => {
            const attemptsForSp = sortedAttempts.filter((a) => a.scheduledPaymentId === sp.id);
            // Backend garantiert max. 1 isCurrentAttempt je ScheduledPayment
            // (Partial-Unique-Index); die DESC-Sortierung macht den jüngsten
            // Treffer defensiv trotzdem eindeutig.
            const ownCurrentAttempt = attemptsForSp.filter((a) => a.isCurrentAttempt)[0] || null;
            // Sammel-Link: `createBundleDispatchPaymentLink` legt EINEN Attempt am
            // Anker-Posten an; alle weiteren Posten stehen nur in dessen
            // `bundleScheduledPaymentIds`. Ohne diesen Zweig zeigte die Tabelle
            // ihnen "Kein Link" und bot einen zweiten Zahlungslink an.
            const bundleAttempt = ownCurrentAttempt
                ? null
                : sortedAttempts.filter(
                      (a) => a.isCurrentAttempt && bundleIdsOf(a).indexOf(sp.id) !== -1,
                  )[0] || null;
            const rawAttempt = ownCurrentAttempt || bundleAttempt;
            // Sammel-Link-Attempts tragen Betrag und Refunds ALLER Bündel-Posten
            // (`MolliePaymentAttemptResolver.refunds`). Für die Zeile zählt nur
            // DIESE Rate: der Server erstattet pro ScheduledPayment gegen deren
            // Kapazität — ActionBar und RefundList bekommen deshalb einen auf die
            // Rate zugeschnittenen Attempt (eigene ID, eigener Betrag, eigene Refunds).
            const currentAttempt =
                rawAttempt && bundleIdsOf(rawAttempt).length > 0
                    ? {
                          ...rawAttempt,
                          scheduledPaymentId: sp.id,
                          amount: Number(sp.paymentAmount || 0),
                          refunds: (rawAttempt.refunds || []).filter(
                              (refund) => refund.scheduledPaymentId === sp.id,
                          ),
                      }
                    : rawAttempt;
            // Historie bleibt strikt am eigenen Posten — der Bündel-Attempt
            // gehoert dem Anker.
            const pastAttempts = attemptsForSp.filter((a) => a !== ownCurrentAttempt);
            const baseStatus = derivePostenStatus({
                scheduledPayment: sp,
                currentAttempt,
                attemptCount: attemptsForSp.length,
                isBookingCanceled: isCanceled,
                isWaitlistBooking,
                // Bündel-Attempts tragen die Refunds ALLER Bündel-Posten —
                // die Zeile bewertet deshalb ihren eigenen `refundedAmount`.
                refundsFromScheduledPayment: Boolean(currentAttempt && bundleIdsOf(currentAttempt).length > 0),
            });
            const status = applyBundleAnnotation(
                baseStatus,
                currentAttempt,
                Boolean(bundleAttempt),
            );
            const fallbackTitle = isDiscount(sp) ? "Gutschrift" : `Rate ${position + 1}`;

            return {
                key: sp.id,
                index: position + 1,
                scheduledPayment: sp,
                title: sp.title || fallbackTitle,
                signedAmount: signedAmount(sp, Number(sp.paymentAmount || 0)),
                currentAttempt,
                pastAttempts,
                status,
                inBundle: Boolean(bundleAttempt),
            };
        });
    }, [scheduledPayments, sortedAttempts, isCanceled, isWaitlistBooking]);

    // ─── Fokus-Zeile: nächster fälliger OFFENER Posten ───────────────────
    // `rows` ist nach paymentDue aufsteigend sortiert → der erste unbezahlte
    // Posten ist der nächste fällige. Er wird nur hervorgehoben, nicht mehr
    // automatisch aufgeklappt — die Kachel startet ruhig, Details auf Klick.
    const focusKey = useMemo(() => {
        const nextOpen = rows.filter((row) => !row.status.isPaid)[0];

        return nextOpen ? nextOpen.key : null;
    }, [rows]);

    const [expandedRowKeys, setExpandedRowKeys] = useState<Array<string>>([]);

    // ─── Offene / bezahlte Posten (Aufteilung wie in PanelCardPayments) ──
    const openRows = rows.filter((row) => !row.status.isPaid);
    const paidRows = rows.filter((row) => row.status.isPaid);
    const totals = useMemo(() => {
        let total = 0;
        let paid = 0;
        let open = 0;
        rows.forEach((row) => {
            const sp = row.scheduledPayment;
            total += row.signedAmount;
            if (row.status.isPaid) {
                paid += signedAmount(sp, Number(sp.paymentAmount || 0) - Number(sp.refundedAmount || 0));
            } else {
                const openValue = sp.openAmount != null ? Number(sp.openAmount) : Number(sp.paymentAmount || 0);
                open += signedAmount(sp, openValue);
            }
        });

        return {total, paid, open};
    }, [rows]);

    // Kurzfassung der Status-Verteilung neben „Offene Posten“, z.B. „1 Link offen · 1 abgelaufen“.
    const openSummary = useMemo(() => {
        const labels: {[kind: string]: string} = {
            open: "Link offen",
            pending: "ausstehend",
            terminal: "abgelaufen",
            noAttempt: "ohne Link",
            canceled: "abgebrochen",
        };
        const counts: {[kind: string]: number} = {};
        openRows.forEach((row) => {
            const kind = labels[row.status.kind] ? row.status.kind : "noAttempt";
            counts[kind] = (counts[kind] || 0) + 1;
        });

        return Object.keys(labels)
            .filter((kind) => counts[kind])
            .map((kind) => `${counts[kind]} ${labels[kind]}`)
            .join(" · ");
    }, [openRows]);

    // ─── Sammel-Link: enthaltene Posten ──────────────────────────────────
    // Serverseitig sammelt der Resolver die offenen Mollie-Posten selbst; die
    // Liste hier dient nur der Vorschau im Modal und der Sichtbarkeitsregel.
    const bundleRows = useMemo(
        () =>
            openRows.filter(
                (row) =>
                    !isDiscount(row.scheduledPayment) &&
                    row.scheduledPayment.paymentProvider === PaymentProvider.Mollie,
            ),
        [openRows],
    );
    const bundleTotal = bundleRows.reduce((sum, row) => {
        const sp = row.scheduledPayment;

        return sum + (sp.openAmount != null ? Number(sp.openAmount) : Number(sp.paymentAmount || 0));
    }, 0);
    const showBundleAction = Boolean(canManagePayments) && !isCanceled && !isWaitlistBooking && !attendeeErased && bundleRows.length >= 2;

    // ─── Mutationen ──────────────────────────────────────────────────────
    const refetchOpts = {
        refetchQueries: [
            {
                query: GetBookingByIdWithCourseLessonBookingsDocument,
                variables: {id: bookingId},
            },
        ],
        awaitRefetchQueries: true,
    };
    const [createNewAttempt, {loading: creatingNew}] = useCreateNewMolliePaymentAttemptFromAdminMutation(
        refetchOpts,
    );
    const [sendBundleLink, {loading: sendingBundle}] = useSendMolliePaymentLinkForBookingFromAdminMutation(
        refetchOpts,
    );

    // Erzeugt einen Zahlungslink für GENAU DIESEN Posten. (Vorher nahm der
    // Empty-State stur `scheduledPayments[0]` — bei mehreren Raten der falsche.)
    const handleCreateAttempt = async (scheduledPaymentId: string) => {
        try {
            await createNewAttempt({variables: {scheduledPaymentId}});
            await refetchBooking();
            message.success("Zahlungslink erstellt", 3);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            message.error(`Zahlungslink konnte nicht erstellt werden: ${msg}`, 5);
        }
    };

    const handleSendBundleLink = async () => {
        try {
            await sendBundleLink({variables: {bookingId}});
            setBundleModalOpen(false);
            await refetchBooking();
            message.success("Sammel-Zahlungslink versendet", 3);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            message.error(msg, 5);
        }
    };

    // ─── Aktionsspalte (zugeklappte Zeile) ───────────────────────────────
    function renderPrimaryAction(row: PostenRow): React.ReactNode {
        const action = row.status.primaryAction;

        // Jede Zeilen-Aktion ruft eine Admin-Mutation — ohne Recht gar nichts
        // anbieten (sonst tote Buttons für Kursleitungen).
        if (!canManagePayments) {
            return null;
        }

        // Nach DSGVO-Löschantrag lehnt der Server einen ersten Zahlungslink ab.
        if (attendeeErased && action === "createLink") {
            return null;
        }

        // Aufgeklappt trägt das Detail die volle ActionBar — die Zeilen-Aktion
        // wäre derselbe Knopf ein zweites Mal. Ohne Attempt zeigt das Detail
        // keine ActionBar, „Zahlungslink erstellen“ bleibt dann in der Zeile.
        if (expandedRowKeys.indexOf(row.key) !== -1 && row.currentAttempt) {
            return null;
        }

        // Offene Bündel-Mitglieder werden über die Anker-Zeile bedient — keine
        // eigene Aktion (sonst zweiter Link auf dieselbe Forderung). Bezahlte
        // Mitglieder dürfen erstatten (Attempt ist auf die Rate zugeschnitten).
        if (row.inBundle && !row.status.isPaid) {
            return null;
        }

        // Posten ohne jeden Attempt: erster Link. Mit Rückfrage, weil die Mutation
        // sofort eine zahlbare Forderung erzeugt (bei Banküberweisung inkl.
        // automatischer Kunden-Mail).
        if (action === "createLink") {
            return (
                <Button
                    size="small"
                    disabled={creatingNew}
                    onClick={() =>
                        Modal.confirm({
                            title: "Zahlungslink erstellen?",
                            content: "Für diesen Posten wird ein Mollie-Zahlungslink erzeugt.",
                            okText: "Link erstellen",
                            cancelText: "Abbrechen",
                            onOk: () => handleCreateAttempt(row.scheduledPayment.id),
                        })
                    }
                    data-testid={`mollie-row-create-link-${row.scheduledPayment.id}`}
                >
                    {"Zahlungslink erstellen"}
                </Button>
            );
        }

        if (!row.currentAttempt || action === "none") {
            return null;
        }

        // refund / resend / refresh / newAttempt laufen über die bestehende
        // ActionBar — keine Duplikation von Mutationen, Modals und Guards
        // (newAttempt bringt dort sein Modal.confirm mit).
        return (
            <ActionBar
                attempt={row.currentAttempt}
                bookingId={bookingId}
                refetchBooking={refetchBooking}
                isBookingCanceled={isCanceled}
                isWaitlistBooking={isWaitlistBooking}
                attendeeErased={attendeeErased}
                variant="primaryOnly"
            />
        );
    }

    const toggleRow = (key: string) => {
        setExpandedRowKeys((prev) =>
            prev.indexOf(key) !== -1 ? prev.filter((k) => k !== key) : prev.concat([key]),
        );
    };

    const dateColumn = (paid: boolean): ColumnsType<PostenRow>[number] => ({
        title: paid ? "Bezahlt am" : "Fällig",
        key: "date",
        width: 150,
        render: (_value: unknown, row: PostenRow) => (
            <div>
                <div className={row.key === focusKey ? css.postenDueFocused : undefined}>
                    {formatDue(paid ? row.scheduledPayment.paymentDone : row.scheduledPayment.paymentDue)}
                </div>
                {row.key === focusKey && (
                    <div className={css.postenSubLine} data-testid="mollie-row-next-due">
                        {"nächster fälliger"}
                    </div>
                )}
            </div>
        ),
    });

    const buildColumns = (paid: boolean): ColumnsType<PostenRow> => [
        dateColumn(paid),
        {
            title: "Posten",
            key: "posten",
            render: (_value: unknown, row: PostenRow) => (
                <span className={row.key === focusKey ? css.postenTitleFocused : undefined}>{row.title}</span>
            ),
        },
        {
            title: "Status",
            key: "status",
            width: 210,
            render: (_value: unknown, row: PostenRow) => {
                const method = row.currentAttempt
                    ? row.currentAttempt.mollieMethod
                    : row.scheduledPayment.mollieMethod;
                const methodLabel = method && method !== "unknown" ? mollieMethodLabel(method) : null;
                // Zweitzeile: Status-Zusatz (Gültigkeit, Versuche, Sammel-Link) und
                // Zahlungsmethode — beides, die Spalte „Methode“ gibt es nicht mehr.
                const subLine = [row.status.secondaryLine, methodLabel].filter(Boolean).join(" · ");

                return (
                    <div>
                        <Tag color={row.status.color} data-testid={`mollie-row-status-${row.key}`}>
                            {row.status.label}
                        </Tag>
                        {subLine && <div className={css.postenSubLine}>{subLine}</div>}
                    </div>
                );
            },
        },
        {
            title: "Betrag",
            key: "amount",
            width: 110,
            align: "right",
            render: (_value: unknown, row: PostenRow) => (
                <span className={css.postenAmount}>{renderAmount({amount: row.signedAmount})}</span>
            ),
        },
        {
            title: "Aktion",
            key: "action",
            width: 190,
            align: "right",
            render: (_value: unknown, row: PostenRow) => (
                <div className={css.postenActions}>{renderPrimaryAction(row)}</div>
            ),
        },
    ];

    const renderTable = (dataSource: Array<PostenRow>, paid: boolean) => (
        <Table<PostenRow>
            className={css.postenTable}
            data-testid={paid ? "mollie-posten-table-paid" : "mollie-posten-table"}
            dataSource={dataSource}
            columns={buildColumns(paid)}
            rowKey="key"
            size="small"
            pagination={false}
            rowClassName={(row) => classNames(row.key === focusKey && css.postenRowFocused)}
            expandable={{
                expandedRowKeys,
                onExpand: (_expanded, row) => toggleRow(row.key),
                // AntD-Standard-Chevron, nur mit Test-ID für Cypress/Jest.
                expandIcon: ({expanded, record}) => (
                    <button
                        type="button"
                        className={classNames(
                            "ant-table-row-expand-icon",
                            expanded ? "ant-table-row-expand-icon-expanded" : "ant-table-row-expand-icon-collapsed",
                        )}
                        aria-label={expanded ? "Details schließen" : "Details anzeigen"}
                        onClick={() => toggleRow(record.key)}
                        data-testid={`mollie-row-details-${record.key}`}
                    />
                ),
                expandedRowRender: (row) => (
                    <PostenDetail
                        scheduledPaymentId={row.scheduledPayment.id}
                        bookingId={bookingId}
                        currentAttempt={row.currentAttempt}
                        pastAttempts={row.pastAttempts}
                        refetchBooking={refetchBooking}
                        isBookingCanceled={isCanceled}
                        isWaitlistBooking={isWaitlistBooking}
                        attendeeErased={attendeeErased}
                        bookingFullySettled={bookingFullySettled}
                        onOpenTimeline={setTimelineSpId}
                        inBundle={row.inBundle && !row.status.isPaid}
                        canManagePayments={Boolean(canManagePayments)}
                    />
                ),
            }}
        />
    );

    // Polling-Hint, sobald IRGENDEIN Posten einen aktiven Mollie-Status hat.
    const showPollingHint = rows.some((row) => row.status.isActive);

    const cardTitle = (
        <span className={css.sectionTitle}>
            {"Zahlung"}
            <span className={css.mollieLogoBadge} aria-label="Mollie">
                {"mollie"}
            </span>
        </span>
    );
    const cardExtra = (
        <div className={css.sumAmount}>
            <span className={css.sumAmountText}>{"Gesamtbetrag"}</span>
            <span className={css.sumAmountValue} data-testid="mollie-kpi-total">
                {renderAmount({amount: totals.total})}
            </span>
        </div>
    );

    return (
        <PanelCard columns={1} title={cardTitle} extra={cardExtra} gapAfter className={css.mollieMode}>
            <div className={css.mollieSection} data-testid="mollie-payment-section">
                <div className={css.subHeader}>
                    <span className={css.subHeaderTitle}>{"Offene Posten"}</span>
                    <span className={css.subHeaderAmount} data-testid="mollie-kpi-open">
                        {renderAmount({amount: totals.open})}
                    </span>
                    {openSummary && (
                        <span className={css.subHeaderSummary} data-testid="mollie-posten-count">
                            {openSummary}
                        </span>
                    )}
                    <span className={css.sectionHeaderSpacer} />
                    {showBundleAction && (
                        <Button size="small" onClick={() => setBundleModalOpen(true)} data-testid="mollie-bundle-link-button">
                            {"Offene Posten gesammelt anfordern"}
                            <Tooltip title={BUNDLE_LINK_TOOLTIP}>
                                <InfoCircleOutlined className={css.sectionHeaderInfoIcon} />
                            </Tooltip>
                        </Button>
                    )}
                </div>
                {openRows.length > 0 ? (
                    renderTable(openRows, false)
                ) : (
                    <div className={css.emptyHint}>{"Keine offenen Posten"}</div>
                )}
                {showPollingHint && (
                    <div className={css.pollingHint} data-testid="mollie-polling-hint" aria-live="polite">
                        {"Automatische Aktualisierung alle 30 s"}
                    </div>
                )}

                <div className={css.subHeader}>
                    <span className={css.subHeaderTitle}>{"Bezahlte Posten"}</span>
                    <span className={css.subHeaderAmount} data-testid="mollie-kpi-paid">
                        {renderAmount({amount: totals.paid})}
                    </span>
                </div>
                {paidRows.length > 0 ? (
                    renderTable(paidRows, true)
                ) : (
                    <div className={css.emptyHint}>{"Noch keine Zahlung eingegangen"}</div>
                )}

                <BundleLinkModal
                    visible={bundleModalOpen}
                    items={bundleRows.map((row) => ({
                        id: row.key,
                        title: row.title,
                        paymentDue: row.scheduledPayment.paymentDue,
                        amount:
                            row.scheduledPayment.openAmount != null
                                ? Number(row.scheduledPayment.openAmount)
                                : Number(row.scheduledPayment.paymentAmount || 0),
                    }))}
                    totalAmount={bundleTotal}
                    bookerEmail={bookerEmail}
                    loading={sendingBundle}
                    onCancel={() => setBundleModalOpen(false)}
                    onConfirm={handleSendBundleLink}
                />

                <PaymentActionTimelineDrawer
                    scheduledPaymentId={timelineSpId}
                    onClose={() => setTimelineSpId(null)}
                />
            </div>
        </PanelCard>
    );
};

export default MolliePaymentSection;
